import React from 'react';
import {
  Leaf,
  BadgeCheck,
  Waves,
  Shirt,
  Users,
  ClipboardList,
  FolderOpen,
  Sparkles,
  Droplets,
  Wind,
  Crown,
  Flower2,
  Flame,
  Gem,
} from 'lucide-react';
import EditableText from './EditableText';
import { DEFAULT_FLYER_COPY, resolveFlyerCopy, patchFlyerCopy } from './flyerCopy';

const enc = (p) => encodeURI(p);

const INFO_ICONS = [Users, ClipboardList, FolderOpen];

/** Shared flyer chrome — used as live previews & thumbnail embeds */
function FlyerShell({ children, className = '', style, footer }) {
  return (
    <div className={`ftl-flyer ftl-root ${className}`} style={style}>
      <div className="ftl-flyer-scale absolute inset-0 flex flex-col">{children}</div>
      {footer}
    </div>
  );
}

function FeatureRow({ items, color, editable, onFeatureChange }) {
  return (
    <div className="flex justify-between gap-2 sm:gap-3 px-1">
      {items.map(({ icon: Icon, label }, index) => (
        <div key={index} className="flex flex-col items-center gap-1.5 text-center flex-1 min-w-0">
          <div className="ftl-feature-icon" style={{ color }}>
            <Icon size={16} strokeWidth={1.6} />
          </div>
          <EditableText
            as="span"
            value={label}
            editable={editable}
            aria-label={`Feature ${index + 1}`}
            onChange={(v) => onFeatureChange?.(index, v)}
            className="text-[0.45rem] sm:text-[0.55rem] font-semibold uppercase tracking-wide leading-tight"
            style={{ color }}
          />
        </div>
      ))}
    </div>
  );
}

function SwatchStack({ colors, sealBg, sealText, editable, onSealChange }) {
  return (
    <div className="relative mt-3 w-[55%] max-w-[11rem]">
      <div className="flex flex-col gap-[0.35rem]">
        {colors.map((c, i) => (
          <div key={`${c}-${i}`} className="ftl-swatch-roll" style={{ background: c }} />
        ))}
      </div>
      <div
        className="ftl-seal absolute -right-10 top-1/2 -translate-y-1/2 text-white"
        style={{ background: sealBg }}
      >
        <EditableText
          as="span"
          value={sealText}
          editable={editable}
          aria-label="Seal text"
          onChange={onSealChange}
          multiline
          className="text-[0.42rem] sm:text-[0.5rem] font-bold uppercase tracking-wider leading-snug px-2"
        />
      </div>
    </div>
  );
}

function InfoBar({ items, color, editable, onInfoChange }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-auto pt-3">
      {items.map(({ icon: Icon, text }, index) => (
        <div key={index} className="flex items-center gap-1.5 min-w-0">
          <Icon size={12} style={{ color }} strokeWidth={1.75} />
          <EditableText
            as="span"
            value={text}
            editable={editable}
            aria-label={`Info line ${index + 1}`}
            onChange={(v) => onInfoChange?.(index, v)}
            className="text-[0.5rem] sm:text-[0.58rem] leading-tight"
            style={{ color: '#4a453c' }}
          />
        </div>
      ))}
    </div>
  );
}

function PerfectForCard({ title, items, accent, editable, onTitleChange, onItemChange }) {
  return (
    <div
      className="absolute bottom-10 right-4 sm:bottom-12 sm:right-6 bg-white/95 backdrop-blur-sm rounded-xl px-3.5 py-3 shadow-lg"
      style={{ border: `1.5px solid ${accent}` }}
    >
      <EditableText
        as="p"
        value={title}
        editable={editable}
        aria-label="Perfect for title"
        onChange={onTitleChange}
        className="ftl-serif text-[0.65rem] sm:text-xs font-semibold tracking-[0.12em] uppercase mb-1.5"
        style={{ color: accent }}
      />
      <ul className="ftl-check-list space-y-0.5 text-[0.55rem] sm:text-[0.65rem]" style={{ color: accent }}>
        {items.map((item, index) => (
          <li key={index}>
            <EditableText
              as="span"
              value={item}
              editable={editable}
              aria-label={`Perfect for item ${index + 1}`}
              onChange={(v) => onItemChange?.(index, v)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function FooterBar({ text, bg, editable, onChange }) {
  return (
    <div
      className="shrink-0 text-center py-2 px-3 text-[0.55rem] sm:text-[0.65rem] font-semibold tracking-[0.18em] uppercase text-white"
      style={{ background: bg }}
    >
      <EditableText as="span" value={text} editable={editable} aria-label="Footer text" onChange={onChange} />
    </div>
  );
}

function getFlyerCopyHandlers(builtInKey, copyProp, onCopyChange) {
  const copy = resolveFlyerCopy(builtInKey, copyProp) || DEFAULT_FLYER_COPY[builtInKey];
  const setField = (path, value) => {
    if (!onCopyChange) return;
    onCopyChange(patchFlyerCopy(copy, path, value));
  };
  return { copy, setField };
}

function buildFeatureItems(icons, labels) {
  return icons.map((icon, i) => ({ icon, label: labels[i] || '' }));
}

function buildInfoItems(lines) {
  return (lines || []).map((text, i) => ({
    icon: INFO_ICONS[i] || Users,
    text,
  }));
}

/* ─────────────────────────────────────────────
   Template 1 — Pleating Suede (reference style)
   ───────────────────────────────────────────── */
export function PleatingSuedeFlyer({
  copy: copyProp,
  onCopyChange,
  editable = false,
  brandName: brandNameProp,
  brandSuffix: brandSuffixProp,
}) {
  const { copy, setField } = getFlyerCopyHandlers('pleating-suede', copyProp, onCopyChange);
  const brandName = brandNameProp || copy.brandName;
  const brandSuffix = brandSuffixProp || copy.brandSuffix;
  const navy = '#1a2744';
  const burgundy = '#6b2c3e';
  const features = buildFeatureItems([Leaf, BadgeCheck, Waves, Shirt], copy.features);
  const swatches = [
    '#c9a09a',
    '#9eb6c9',
    '#c4b89a',
    '#c45c8a',
    '#c9a84a',
    '#5c3a2e',
    '#f0ebe3',
    '#3d3a38',
    '#2f4a3a',
  ];

  return (
    <FlyerShell>
      <div className="flex flex-1 min-h-0">
        <div className="w-[42%] flex flex-col px-4 sm:px-6 py-4 sm:py-5 bg-[#f3efe6]">
          <div>
            <EditableText
              as="h2"
              value={brandName}
              editable={editable}
              aria-label="Brand name"
              onChange={(v) => setField('brandName', v)}
              className="ftl-serif text-2xl sm:text-3xl font-bold tracking-tight"
              style={{ color: navy }}
            />
            <div className="flex items-center gap-2 mt-0.5 mb-2">
              <span className="h-px flex-1" style={{ background: navy, opacity: 0.35 }} />
              <EditableText
                as="span"
                value={brandSuffix}
                editable={editable}
                aria-label="Brand suffix"
                onChange={(v) => setField('brandSuffix', v)}
                className="text-[0.55rem] sm:text-[0.65rem] font-semibold tracking-[0.35em]"
                style={{ color: navy }}
              />
              <span className="h-px flex-1" style={{ background: navy, opacity: 0.35 }} />
            </div>
            <EditableText
              as="p"
              value={copy.collectionLabel}
              editable={editable}
              aria-label="Collection label"
              onChange={(v) => setField('collectionLabel', v)}
              className="ftl-script text-base sm:text-lg"
              style={{ color: '#8a6b2f' }}
            />
            <EditableText
              as="h3"
              value={copy.productTitle}
              editable={editable}
              aria-label="Product title"
              onChange={(v) => setField('productTitle', v)}
              className="ftl-serif text-xl sm:text-2xl md:text-3xl font-bold leading-none mt-0.5"
              style={{ color: burgundy }}
            />
            <div className="flex items-center gap-2 mt-2 mb-4">
              <span className="h-px w-6" style={{ background: navy, opacity: 0.4 }} />
              <span className="w-1 h-1 rounded-full" style={{ background: navy }} />
              <EditableText
                as="span"
                value={copy.audienceLine}
                editable={editable}
                aria-label="Audience line"
                onChange={(v) => setField('audienceLine', v)}
                className="text-[0.5rem] sm:text-[0.58rem] font-semibold tracking-[0.22em] uppercase"
                style={{ color: navy }}
              />
              <span className="w-1 h-1 rounded-full" style={{ background: navy }} />
              <span className="h-px w-6" style={{ background: navy, opacity: 0.4 }} />
            </div>
          </div>

          <FeatureRow
            items={features}
            color={navy}
            editable={editable}
            onFeatureChange={(i, v) => setField(`features.${i}`, v)}
          />

          <EditableText
            as="p"
            value={copy.tagline}
            editable={editable}
            aria-label="Tagline"
            onChange={(v) => setField('tagline', v)}
            className="ftl-serif text-[0.65rem] sm:text-xs italic mt-3 mb-1"
            style={{ color: navy }}
          />

          <SwatchStack
            colors={swatches}
            sealBg={burgundy}
            sealText={copy.sealText}
            editable={editable}
            onSealChange={(v) => setField('sealText', v)}
          />

          <InfoBar
            color={navy}
            items={buildInfoItems(copy.infoLines)}
            editable={editable}
            onInfoChange={(i, v) => setField(`infoLines.${i}`, v)}
          />
        </div>

        <div className="relative flex-1 min-w-0 overflow-hidden bg-[#e8ddd4]">
          <img
            src={enc('/Female/Dresses/Long Dress.png')}
            alt="Pleating suede lifestyle"
            className="absolute inset-0 w-full h-full object-cover object-[center_15%]"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#f3efe6]/20" />
          <EditableText
            as="p"
            value={copy.heroQuote}
            editable={editable}
            aria-label="Hero quote"
            onChange={(v) => setField('heroQuote', v)}
            className="ftl-script absolute top-4 right-4 sm:top-6 sm:right-6 text-lg sm:text-2xl drop-shadow-sm"
            style={{ color: navy }}
          />
          <PerfectForCard
            title={copy.perfectForTitle}
            accent={navy}
            items={copy.perfectFor}
            editable={editable}
            onTitleChange={(v) => setField('perfectForTitle', v)}
            onItemChange={(i, v) => setField(`perfectFor.${i}`, v)}
          />
        </div>
      </div>
      <FooterBar
        text={copy.footerText}
        bg={navy}
        editable={editable}
        onChange={(v) => setField('footerText', v)}
      />
    </FlyerShell>
  );
}

/* ─────────────────────────────────────────────
   Template 2 — Georgette Glow
   ───────────────────────────────────────────── */
export function GeorgetteGlowFlyer({
  copy: copyProp,
  onCopyChange,
  editable = false,
  brandName: brandNameProp,
  brandSuffix: brandSuffixProp,
}) {
  const { copy, setField } = getFlyerCopyHandlers('georgette-glow', copyProp, onCopyChange);
  const brandName = brandNameProp || copy.brandName;
  const brandSuffix = brandSuffixProp || copy.brandSuffix;
  const teal = '#1a4a4a';
  const coral = '#b85c4a';
  const features = buildFeatureItems([Wind, Sparkles, Droplets, Flower2], copy.features);
  const swatches = ['#e8d5c4', '#c9b8a8', '#a8c4b8', '#7a9e9a', '#d4a090', '#f5efe6', '#5a7068', '#c4a878'];

  return (
    <FlyerShell style={{ '--ftl-cream': '#f6f1ea' }}>
      <div className="flex flex-1 min-h-0">
        <div className="w-[42%] flex flex-col px-4 sm:px-6 py-4 sm:py-5 bg-[#f6f1ea]">
          <div>
            <EditableText
              as="h2"
              value={brandName}
              editable={editable}
              aria-label="Brand name"
              onChange={(v) => setField('brandName', v)}
              className="ftl-serif text-2xl sm:text-3xl font-bold"
              style={{ color: teal }}
            />
            <div className="flex items-center gap-2 mt-0.5 mb-2">
              <span className="h-px flex-1 bg-current opacity-30" style={{ color: teal }} />
              <EditableText
                as="span"
                value={brandSuffix}
                editable={editable}
                aria-label="Brand suffix"
                onChange={(v) => setField('brandSuffix', v)}
                className="text-[0.55rem] font-semibold tracking-[0.35em]"
                style={{ color: teal }}
              />
              <span className="h-px flex-1 bg-current opacity-30" style={{ color: teal }} />
            </div>
            <EditableText
              as="p"
              value={copy.collectionLabel}
              editable={editable}
              aria-label="Collection label"
              onChange={(v) => setField('collectionLabel', v)}
              className="ftl-script text-base sm:text-lg"
              style={{ color: coral }}
            />
            <EditableText
              as="h3"
              value={copy.productTitle}
              editable={editable}
              aria-label="Product title"
              onChange={(v) => setField('productTitle', v)}
              className="ftl-serif text-xl sm:text-2xl md:text-3xl font-bold leading-none"
              style={{ color: teal }}
            />
            <EditableText
              as="p"
              value={copy.audienceLine}
              editable={editable}
              aria-label="Audience line"
              onChange={(v) => setField('audienceLine', v)}
              className="text-[0.5rem] sm:text-[0.58rem] font-semibold tracking-[0.22em] uppercase mt-2 mb-4"
              style={{ color: teal }}
            />
          </div>
          <FeatureRow
            items={features}
            color={teal}
            editable={editable}
            onFeatureChange={(i, v) => setField(`features.${i}`, v)}
          />
          <EditableText
            as="p"
            value={copy.tagline}
            editable={editable}
            aria-label="Tagline"
            onChange={(v) => setField('tagline', v)}
            className="ftl-serif text-[0.65rem] sm:text-xs italic mt-3"
            style={{ color: teal }}
          />
          <SwatchStack
            colors={swatches}
            sealBg={coral}
            sealText={copy.sealText}
            editable={editable}
            onSealChange={(v) => setField('sealText', v)}
          />
          <InfoBar
            color={teal}
            items={buildInfoItems(copy.infoLines)}
            editable={editable}
            onInfoChange={(i, v) => setField(`infoLines.${i}`, v)}
          />
        </div>
        <div className="relative flex-1 min-w-0 bg-[#e8ddd0]">
          <img
            src={enc('/Female/Dresses/Halter dress.png')}
            alt="Georgette garment"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a4a4a]/25 via-transparent to-transparent" />
          <EditableText
            as="p"
            value={copy.heroQuote}
            editable={editable}
            aria-label="Hero quote"
            onChange={(v) => setField('heroQuote', v)}
            className="ftl-script absolute top-4 right-4 sm:top-6 sm:right-6 text-lg sm:text-2xl text-white drop-shadow"
          />
          <PerfectForCard
            title={copy.perfectForTitle}
            accent={teal}
            items={copy.perfectFor}
            editable={editable}
            onTitleChange={(v) => setField('perfectForTitle', v)}
            onItemChange={(i, v) => setField(`perfectFor.${i}`, v)}
          />
        </div>
      </div>
      <FooterBar
        text={copy.footerText}
        bg={teal}
        editable={editable}
        onChange={(v) => setField('footerText', v)}
      />
    </FlyerShell>
  );
}

/* ─────────────────────────────────────────────
   Template 3 — Linen Essentials
   ───────────────────────────────────────────── */
export function LinenEssentialsFlyer({
  copy: copyProp,
  onCopyChange,
  editable = false,
  brandName: brandNameProp,
  brandSuffix: brandSuffixProp,
}) {
  const { copy, setField } = getFlyerCopyHandlers('linen-essentials', copyProp, onCopyChange);
  const brandName = brandNameProp || copy.brandName;
  const brandSuffix = brandSuffixProp || copy.brandSuffix;
  const ink = '#2c2a26';
  const clay = '#9a6b4f';
  const features = buildFeatureItems([Leaf, Wind, BadgeCheck, Shirt], copy.features);
  const swatches = ['#e8e0d4', '#c9bba8', '#a89078', '#8a735c', '#6b5848', '#d4c8b0', '#f5f0e6', '#4a4038'];

  return (
    <FlyerShell>
      <div className="flex flex-1 min-h-0 flex-row-reverse">
        <div className="w-[42%] flex flex-col px-4 sm:px-6 py-4 sm:py-5 bg-[#f7f4ee]">
          <div>
            <EditableText
              as="h2"
              value={brandName}
              editable={editable}
              aria-label="Brand name"
              onChange={(v) => setField('brandName', v)}
              className="ftl-serif text-2xl sm:text-3xl font-bold"
              style={{ color: ink }}
            />
            <div className="flex items-center gap-2 mt-0.5 mb-2">
              <span className="h-px flex-1 opacity-30" style={{ background: ink }} />
              <EditableText
                as="span"
                value={brandSuffix}
                editable={editable}
                aria-label="Brand suffix"
                onChange={(v) => setField('brandSuffix', v)}
                className="text-[0.55rem] font-semibold tracking-[0.35em]"
                style={{ color: ink }}
              />
              <span className="h-px flex-1 opacity-30" style={{ background: ink }} />
            </div>
            <EditableText
              as="p"
              value={copy.collectionLabel}
              editable={editable}
              aria-label="Collection label"
              onChange={(v) => setField('collectionLabel', v)}
              className="ftl-script text-base sm:text-lg"
              style={{ color: clay }}
            />
            <EditableText
              as="h3"
              value={copy.productTitle}
              editable={editable}
              aria-label="Product title"
              onChange={(v) => setField('productTitle', v)}
              className="ftl-serif text-xl sm:text-2xl md:text-3xl font-bold leading-none"
              style={{ color: ink }}
            />
            <EditableText
              as="p"
              value={copy.audienceLine}
              editable={editable}
              aria-label="Audience line"
              onChange={(v) => setField('audienceLine', v)}
              className="text-[0.5rem] sm:text-[0.58rem] font-semibold tracking-[0.22em] uppercase mt-2 mb-4"
              style={{ color: ink }}
            />
          </div>
          <FeatureRow
            items={features}
            color={ink}
            editable={editable}
            onFeatureChange={(i, v) => setField(`features.${i}`, v)}
          />
          <EditableText
            as="p"
            value={copy.tagline}
            editable={editable}
            aria-label="Tagline"
            onChange={(v) => setField('tagline', v)}
            className="ftl-serif text-[0.65rem] sm:text-xs italic mt-3"
            style={{ color: ink }}
          />
          <SwatchStack
            colors={swatches}
            sealBg={clay}
            sealText={copy.sealText}
            editable={editable}
            onSealChange={(v) => setField('sealText', v)}
          />
          <InfoBar
            color={ink}
            items={buildInfoItems(copy.infoLines)}
            editable={editable}
            onInfoChange={(i, v) => setField(`infoLines.${i}`, v)}
          />
        </div>
        <div className="relative flex-1 min-w-0">
          <img
            src={enc('/Female/Coord Sets/Crop top skirt.png')}
            alt="Linen co-ord"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <EditableText
            as="p"
            value={copy.heroQuote}
            editable={editable}
            aria-label="Hero quote"
            onChange={(v) => setField('heroQuote', v)}
            className="ftl-script absolute top-4 left-4 sm:top-6 sm:left-6 text-lg sm:text-2xl"
            style={{ color: ink }}
          />
          <PerfectForCard
            title={copy.perfectForTitle}
            accent={ink}
            items={copy.perfectFor}
            editable={editable}
            onTitleChange={(v) => setField('perfectForTitle', v)}
            onItemChange={(i, v) => setField(`perfectFor.${i}`, v)}
          />
        </div>
      </div>
      <FooterBar
        text={copy.footerText}
        bg={ink}
        editable={editable}
        onChange={(v) => setField('footerText', v)}
      />
    </FlyerShell>
  );
}

/* ─────────────────────────────────────────────
   Template 4 — Velvet Royale
   ───────────────────────────────────────────── */
export function VelvetRoyaleFlyer({
  copy: copyProp,
  onCopyChange,
  editable = false,
  brandName: brandNameProp,
  brandSuffix: brandSuffixProp,
}) {
  const { copy, setField } = getFlyerCopyHandlers('velvet-royale', copyProp, onCopyChange);
  const brandName = brandNameProp || copy.brandName;
  const brandSuffix = brandSuffixProp || copy.brandSuffix;
  const plum = '#2a1528';
  const gold = '#b8956c';
  const features = buildFeatureItems([Crown, Gem, Flame, Sparkles], copy.features);
  const swatches = ['#4a1a2e', '#1a2a4a', '#2a1a3a', '#5a2a1a', '#1a3a2a', '#3a2a1a', '#f0e8d8', '#0a0a0c'];

  return (
    <FlyerShell style={{ background: '#1a1218' }}>
      <div className="flex flex-1 min-h-0">
        <div className="w-[42%] flex flex-col px-4 sm:px-6 py-4 sm:py-5" style={{ background: '#241820' }}>
          <div>
            <EditableText
              as="h2"
              value={brandName}
              editable={editable}
              aria-label="Brand name"
              onChange={(v) => setField('brandName', v)}
              className="ftl-serif text-2xl sm:text-3xl font-bold text-[#f3ebe0]"
            />
            <div className="flex items-center gap-2 mt-0.5 mb-2">
              <span className="h-px flex-1 opacity-40" style={{ background: gold }} />
              <EditableText
                as="span"
                value={brandSuffix}
                editable={editable}
                aria-label="Brand suffix"
                onChange={(v) => setField('brandSuffix', v)}
                className="text-[0.55rem] font-semibold tracking-[0.35em]"
                style={{ color: gold }}
              />
              <span className="h-px flex-1 opacity-40" style={{ background: gold }} />
            </div>
            <EditableText
              as="p"
              value={copy.collectionLabel}
              editable={editable}
              aria-label="Collection label"
              onChange={(v) => setField('collectionLabel', v)}
              className="ftl-script text-base sm:text-lg"
              style={{ color: gold }}
            />
            <EditableText
              as="h3"
              value={copy.productTitle}
              editable={editable}
              aria-label="Product title"
              onChange={(v) => setField('productTitle', v)}
              className="ftl-serif text-xl sm:text-2xl md:text-3xl font-bold leading-none text-[#f3ebe0]"
            />
            <EditableText
              as="p"
              value={copy.audienceLine}
              editable={editable}
              aria-label="Audience line"
              onChange={(v) => setField('audienceLine', v)}
              className="text-[0.5rem] sm:text-[0.58rem] font-semibold tracking-[0.22em] uppercase mt-2 mb-4"
              style={{ color: gold }}
            />
          </div>
          <FeatureRow
            items={features}
            color={gold}
            editable={editable}
            onFeatureChange={(i, v) => setField(`features.${i}`, v)}
          />
          <EditableText
            as="p"
            value={copy.tagline}
            editable={editable}
            aria-label="Tagline"
            onChange={(v) => setField('tagline', v)}
            className="ftl-serif text-[0.65rem] sm:text-xs italic mt-3 text-[#d4c8b8]"
          />
          <SwatchStack
            colors={swatches}
            sealBg={gold}
            sealText={copy.sealText}
            editable={editable}
            onSealChange={(v) => setField('sealText', v)}
          />
          <InfoBar
            color={gold}
            items={buildInfoItems(copy.infoLines)}
            editable={editable}
            onInfoChange={(i, v) => setField(`infoLines.${i}`, v)}
          />
        </div>
        <div className="relative flex-1 min-w-0">
          <img
            src={enc('/Female/Dresses/Long Dress.png')}
            alt="Velvet evening look"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#241820]/50 via-transparent to-transparent" />
          <EditableText
            as="p"
            value={copy.heroQuote}
            editable={editable}
            aria-label="Hero quote"
            onChange={(v) => setField('heroQuote', v)}
            className="ftl-script absolute top-4 right-4 sm:top-6 sm:right-6 text-lg sm:text-2xl text-[#f3ebe0] drop-shadow"
          />
          <PerfectForCard
            title={copy.perfectForTitle}
            accent={plum}
            items={copy.perfectFor}
            editable={editable}
            onTitleChange={(v) => setField('perfectForTitle', v)}
            onItemChange={(i, v) => setField(`perfectFor.${i}`, v)}
          />
        </div>
      </div>
      <FooterBar
        text={copy.footerText}
        bg={plum}
        editable={editable}
        onChange={(v) => setField('footerText', v)}
      />
    </FlyerShell>
  );
}

/* ─────────────────────────────────────────────
   Template 5 — Cotton Story
   ───────────────────────────────────────────── */
export function CottonStoryFlyer({
  copy: copyProp,
  onCopyChange,
  editable = false,
  brandName: brandNameProp,
  brandSuffix: brandSuffixProp,
}) {
  const { copy, setField } = getFlyerCopyHandlers('cotton-story', copyProp, onCopyChange);
  const brandName = brandNameProp || copy.brandName;
  const brandSuffix = brandSuffixProp || copy.brandSuffix;
  const blue = '#234e6d';
  const rust = '#a65d3f';
  const features = buildFeatureItems([Leaf, Droplets, Waves, Shirt], copy.features);
  const swatches = ['#2a5a7a', '#c45c3a', '#e8dcc8', '#1a3a4a', '#8a9a6a', '#f5f0e8', '#5a3a2a', '#7a8a9a'];

  return (
    <FlyerShell>
      <div className="flex flex-1 min-h-0">
        <div className="w-[42%] flex flex-col px-4 sm:px-6 py-4 sm:py-5 bg-[#f2eee6]">
          <div>
            <EditableText
              as="h2"
              value={brandName}
              editable={editable}
              aria-label="Brand name"
              onChange={(v) => setField('brandName', v)}
              className="ftl-serif text-2xl sm:text-3xl font-bold"
              style={{ color: blue }}
            />
            <div className="flex items-center gap-2 mt-0.5 mb-2">
              <span className="h-px flex-1 opacity-30" style={{ background: blue }} />
              <EditableText
                as="span"
                value={brandSuffix}
                editable={editable}
                aria-label="Brand suffix"
                onChange={(v) => setField('brandSuffix', v)}
                className="text-[0.55rem] font-semibold tracking-[0.35em]"
                style={{ color: blue }}
              />
              <span className="h-px flex-1 opacity-30" style={{ background: blue }} />
            </div>
            <EditableText
              as="p"
              value={copy.collectionLabel}
              editable={editable}
              aria-label="Collection label"
              onChange={(v) => setField('collectionLabel', v)}
              className="ftl-script text-base sm:text-lg"
              style={{ color: rust }}
            />
            <EditableText
              as="h3"
              value={copy.productTitle}
              editable={editable}
              aria-label="Product title"
              onChange={(v) => setField('productTitle', v)}
              className="ftl-serif text-xl sm:text-2xl md:text-3xl font-bold leading-none"
              style={{ color: blue }}
            />
            <EditableText
              as="p"
              value={copy.audienceLine}
              editable={editable}
              aria-label="Audience line"
              onChange={(v) => setField('audienceLine', v)}
              className="text-[0.5rem] sm:text-[0.58rem] font-semibold tracking-[0.22em] uppercase mt-2 mb-4"
              style={{ color: blue }}
            />
          </div>
          <FeatureRow
            items={features}
            color={blue}
            editable={editable}
            onFeatureChange={(i, v) => setField(`features.${i}`, v)}
          />
          <EditableText
            as="p"
            value={copy.tagline}
            editable={editable}
            aria-label="Tagline"
            onChange={(v) => setField('tagline', v)}
            className="ftl-serif text-[0.65rem] sm:text-xs italic mt-3"
            style={{ color: blue }}
          />
          <SwatchStack
            colors={swatches}
            sealBg={rust}
            sealText={copy.sealText}
            editable={editable}
            onSealChange={(v) => setField('sealText', v)}
          />
          <InfoBar
            color={blue}
            items={buildInfoItems(copy.infoLines)}
            editable={editable}
            onInfoChange={(i, v) => setField(`infoLines.${i}`, v)}
          />
        </div>
        <div className="relative flex-1 min-w-0">
          <img
            src={enc('/Female/Tops/Halter.png')}
            alt="Cotton top"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <EditableText
            as="p"
            value={copy.heroQuote}
            editable={editable}
            aria-label="Hero quote"
            onChange={(v) => setField('heroQuote', v)}
            className="ftl-script absolute top-4 right-4 sm:top-6 sm:right-6 text-lg sm:text-2xl"
            style={{ color: blue }}
          />
          <PerfectForCard
            title={copy.perfectForTitle}
            accent={blue}
            items={copy.perfectFor}
            editable={editable}
            onTitleChange={(v) => setField('perfectForTitle', v)}
            onItemChange={(i, v) => setField(`perfectFor.${i}`, v)}
          />
        </div>
      </div>
      <FooterBar
        text={copy.footerText}
        bg={blue}
        editable={editable}
        onChange={(v) => setField('footerText', v)}
      />
    </FlyerShell>
  );
}

export const FABRIC_TEMPLATE_COMPONENTS = {
  'pleating-suede': PleatingSuedeFlyer,
  'georgette-glow': GeorgetteGlowFlyer,
  'linen-essentials': LinenEssentialsFlyer,
  'velvet-royale': VelvetRoyaleFlyer,
  'cotton-story': CottonStoryFlyer,
};

/** @deprecated Prefer API-loaded templates via getFabricTemplates + hydrateFabricTemplate */
export const FABRIC_TEMPLATES = [
  {
    id: 'pleating-suede',
    name: 'Pleating Suede',
    category: 'Imported',
    tagline: 'Split-layout promo with swatches & lifestyle',
    tags: ['Women', 'Pleated', 'Premium'],
    thumbnail: '/fabric-templates/pleating-suede-ref.png',
    designImage: enc('/Female/Dresses/Long Dress.png'),
    productType: "Pleated maxi dress for women's western wear",
    Component: PleatingSuedeFlyer,
    accent: '#6b2c3e',
    builtInKey: 'pleating-suede',
    copy: DEFAULT_FLYER_COPY['pleating-suede'],
  },
  {
    id: 'georgette-glow',
    name: 'Soft Georgette',
    category: 'Lightweight',
    tagline: 'Airy drape story for summer collections',
    tags: ['Sheer', 'Daywear', 'Occasion'],
    designImage: enc('/Female/Dresses/Halter dress.png'),
    productType: 'Soft georgette halter dress for daywear and occasion',
    Component: GeorgetteGlowFlyer,
    accent: '#1a4a4a',
    builtInKey: 'georgette-glow',
    copy: DEFAULT_FLYER_COPY['georgette-glow'],
  },
  {
    id: 'linen-essentials',
    name: 'Linen Blend',
    category: 'Natural',
    tagline: 'Mirrored layout with earthy neutrals',
    tags: ['Natural', 'Contemporary', 'Workwear'],
    designImage: enc('/Female/Coord Sets/Crop top skirt.png'),
    productType: 'Linen blend co-ord set for contemporary wear',
    Component: LinenEssentialsFlyer,
    accent: '#9a6b4f',
    builtInKey: 'linen-essentials',
    copy: DEFAULT_FLYER_COPY['linen-essentials'],
  },
  {
    id: 'velvet-royale',
    name: 'Silk Velvet',
    category: 'Evening',
    tagline: 'Dark dramatic flyer for bridal & festive',
    tags: ['Bridal', 'Festive', 'Couture'],
    designImage: enc('/Female/Dresses/Long Dress.png'),
    productType: 'Silk velvet evening gown for bridal and festive',
    Component: VelvetRoyaleFlyer,
    accent: '#b8956c',
    builtInKey: 'velvet-royale',
    copy: DEFAULT_FLYER_COPY['velvet-royale'],
  },
  {
    id: 'cotton-story',
    name: 'Print Cotton',
    category: 'Everyday',
    tagline: 'Clean commercial layout for volume fabrics',
    tags: ['Cotton', 'Kids', 'Volume'],
    designImage: enc('/Female/Tops/Halter.png'),
    productType: 'Print cotton top for everyday wear',
    Component: CottonStoryFlyer,
    accent: '#234e6d',
    builtInKey: 'cotton-story',
    copy: DEFAULT_FLYER_COPY['cotton-story'],
  },
];

/** Map API template records into UI-ready objects (with optional live flyer Component). */
export function hydrateFabricTemplate(apiTemplate) {
  if (!apiTemplate) return null;
  const builtInKey = apiTemplate.builtInKey || null;
  const Component = builtInKey ? FABRIC_TEMPLATE_COMPONENTS[builtInKey] || null : null;
  const templateKind = apiTemplate.templateKind === 'html' ? 'html' : 'image';
  const htmlContent = templateKind === 'html' ? apiTemplate.htmlContent || '' : '';
  const thumbnail = apiTemplate.thumbnailUrl || apiTemplate.thumbnail || '';
  const designImage = apiTemplate.designImageUrl || apiTemplate.designImage || thumbnail;
  const copy = builtInKey
    ? resolveFlyerCopy(builtInKey, apiTemplate.copy)
    : apiTemplate.copy && typeof apiTemplate.copy === 'object'
      ? apiTemplate.copy
      : null;
  return {
    id: apiTemplate.id || apiTemplate._id || builtInKey,
    _id: apiTemplate._id || apiTemplate.id,
    name: apiTemplate.name,
    category: apiTemplate.category,
    tagline: apiTemplate.tagline || '',
    tags: Array.isArray(apiTemplate.tags) ? apiTemplate.tags : [],
    templateKind,
    htmlContent,
    thumbnail,
    designImage,
    productType: apiTemplate.productType || apiTemplate.name,
    accent: apiTemplate.accent || '#1a2744',
    builtInKey,
    isBuiltIn: Boolean(apiTemplate.isBuiltIn),
    createdBy: apiTemplate.createdBy || null,
    canManage: Boolean(apiTemplate.canManage),
    copy,
    Component,
  };
}
